import type { FormatParser, SSEEvent } from './const';

interface ToolCallState {
  name: string;
  arguments: string;
}

/**
 * OpenAI 格式解析器
 * 负责解析 OpenAI 格式的 SSE 消息
 */
export class OpenAIParser implements FormatParser {
  private toolCallsMap = new Map<string, ToolCallState>();
  private indexToToolIdMap = new Map<number, string>();
  private responseItemToToolIdMap = new Map<string, string>();
  private recentTextDeltaSignatures: string[] = [];
  private recentTextDeltaSignatureSet = new Set<string>();
  private debug: boolean;
  private hasSeenThinking = false;

  constructor(debug = false) {
    this.debug = debug;
  }

  reset() {
    this.toolCallsMap.clear();
    this.indexToToolIdMap.clear();
    this.responseItemToToolIdMap.clear();
    this.recentTextDeltaSignatures = [];
    this.recentTextDeltaSignatureSet.clear();
    this.hasSeenThinking = false;
  }

  parseLine(line: string): SSEEvent | null {
    if (!line.trim()) {
      return null;
    }

    try {
      const parsed = JSON.parse(line);
      return this.parseEvent(parsed);
    } catch (e) {
      if (this.debug) {
        console.warn('[OpenAIParser] 解析 SSE 行失败:', line, e);
      }
      return null;
    }
  }

  private parseEvent(parsed: any): SSEEvent | null {
    if (parsed.data?.type === 'response_started') {
      return { type: 'response_started' };
    }

    if (parsed.data?.type === 'output_text_delta' && parsed.data.delta) {
      this.rememberTextDeltaSignature(parsed.data.providerData, parsed.data.delta);
      return {
        type: 'text_delta',
        data: { delta: parsed.data.delta },
      };
    }

    if (parsed.type === 'error' || parsed.data?.type === 'error' || parsed.name === 'error') {
      const errorMessage =
        parsed.error ||
        parsed.data?.error ||
        parsed.message ||
        parsed.data?.message ||
        parsed.item?.error ||
        parsed.item?.message ||
        '执行出错';

      return {
        type: 'error',
        data: { error: errorMessage },
      };
    }

    if (parsed.type === 'raw_model_stream_event' && parsed.data?.type === 'model' && parsed.data.event) {
      try {
        const eventData = typeof parsed.data.event === 'string'
          ? JSON.parse(parsed.data.event)
          : parsed.data.event;

        return this.parseModelEvent(eventData);
      } catch (e) {
        if (this.debug) {
          console.warn('[OpenAIParser] 解析 model event 失败:', e);
        }
      }
    }

    if (parsed.data?.type === 'response_done' && parsed.data.response?.output) {
      this.hydrateToolCallsFromResponseOutput(parsed.data.response.output);

      const text = parsed.data.response.output
        .flatMap((item: any) => Array.isArray(item?.content) ? item.content : [])
        .map((item: any) => typeof item?.text === 'string' ? item.text : '')
        .join('');

      return {
        type: 'response_done',
        data: {
          text,
          responseId: typeof parsed.data.response.id === 'string' ? parsed.data.response.id : undefined,
        },
      };
    }

    if (this.isRunItemStreamEvent(parsed)) {
      const toolEvent = this.parseRunItemToolEvent(parsed);
      if (toolEvent) {
        return toolEvent;
      }
    }

    return null;
  }

  private parseModelEvent(eventData: any): SSEEvent | null {
    if (typeof eventData?.type === 'string' && eventData.type.startsWith('response.')) {
      return this.parseResponsesModelEvent(eventData);
    }

    const choice = eventData.choices?.[0];
    if (!choice) {
      return null;
    }

    const delta = choice.delta;
    if (!delta) {
      return null;
    }

    if (delta.thinking_blocks && Array.isArray(delta.thinking_blocks)) {
      const thinkingBlock = delta.thinking_blocks[0];

      if (thinkingBlock?.signature) {
        this.hasSeenThinking = false;
        return {
          type: 'thinking_done',
        };
      }

      if (thinkingBlock?.thinking) {
        this.hasSeenThinking = true;
        return {
          type: 'thinking_delta',
          data: { delta: thinkingBlock.thinking },
        };
      }
    }

    if (delta.reasoning_content) {
      this.hasSeenThinking = true;
      return {
        type: 'thinking_delta',
        data: { delta: delta.reasoning_content },
      };
    }

    if (delta.content && this.hasSeenThinking) {
      this.hasSeenThinking = false;
      return {
        type: 'thinking_done',
      };
    }

    if (delta.tool_calls && Array.isArray(delta.tool_calls)) {
      const toolCall = delta.tool_calls[0];
      if (!toolCall) {
        return null;
      }

      const index = toolCall.index ?? 0;
      const toolId = toolCall.id;
      const toolName = toolCall.function?.name;
      const argsFragment = toolCall.function?.arguments || '';

      if (toolId && toolName) {
        this.toolCallsMap.set(toolId, { name: toolName, arguments: '' });
        this.indexToToolIdMap.set(index, toolId);

        return {
          type: 'tool_call_start',
          data: { toolId, toolName, index },
        };
      }

      if (argsFragment) {
        const existingToolId = this.indexToToolIdMap.get(index);
        if (existingToolId && this.toolCallsMap.has(existingToolId)) {
          const toolCallInfo = this.toolCallsMap.get(existingToolId)!;
          toolCallInfo.arguments += argsFragment;

          return {
            type: 'tool_call_delta',
            data: {
              toolId: existingToolId,
              toolName: toolCallInfo.name,
              partialArguments: toolCallInfo.arguments,
            },
          };
        }
      }
    }

    return null;
  }

  private parseResponsesModelEvent(eventData: any): SSEEvent | null {
    switch (eventData.type) {
      case 'response.output_item.added':
        return this.parseResponseOutputItemAdded(eventData);

      case 'response.reasoning_summary_text.delta':
        if (eventData.delta) {
          this.hasSeenThinking = true;
          return {
            type: 'thinking_delta',
            data: { delta: eventData.delta },
          };
        }
        return null;

      case 'response.reasoning_summary_text.done':
      case 'response.reasoning_summary_part.done':
        this.hasSeenThinking = false;
        return { type: 'thinking_done' };

      case 'response.function_call_arguments.delta':
        return this.parseResponseFunctionCallArgumentsDelta(eventData);

      case 'response.function_call_arguments.done':
        return this.parseResponseFunctionCallArgumentsDone(eventData);

      case 'response.output_item.done':
        return this.parseResponseOutputItemDone(eventData);

      case 'response.output_text.delta':
        if (!eventData.delta || this.hasRecentTextDeltaSignature(eventData, eventData.delta)) {
          return null;
        }
        return {
          type: 'text_delta',
          data: { delta: eventData.delta },
        };

      default:
        return null;
    }
  }

  private parseResponseOutputItemAdded(eventData: any): SSEEvent | null {
    const item = eventData.item;
    if (!item) {
      return null;
    }

    if (item.type === 'reasoning') {
      this.hasSeenThinking = true;
      return null;
    }

    if (item.type !== 'function_call') {
      return null;
    }

    const toolId = this.getFunctionCallId(item);
    const toolName = item.name;
    if (!toolId || !toolName) {
      return null;
    }

    this.toolCallsMap.set(toolId, { name: toolName, arguments: item.arguments || '' });
    if (item.id) {
      this.responseItemToToolIdMap.set(item.id, toolId);
    }

    return {
      type: 'tool_call_start',
      data: {
        toolId,
        requestId: item.id,
        callId: item.callId || item.call_id,
        toolName,
        index: eventData.output_index ?? 0,
      },
    };
  }

  private isRunItemStreamEvent(eventData: any): boolean {
    return eventData?.type === 'run_item_stream_event' ||
      eventData?.name === 'tool_called' ||
      eventData?.name === 'tool_output';
  }

  private parseRunItemToolEvent(eventData: any): SSEEvent | null {
    const rawItem = eventData.item?.rawItem ?? eventData.item;
    if (!rawItem) {
      return null;
    }

    if (eventData.name === 'tool_called' && rawItem.type === 'function_call') {
      return {
        type: 'tool_called',
        data: {
          toolId: this.getFunctionCallId(rawItem),
          requestId: rawItem.id,
          callId: rawItem.callId || rawItem.call_id,
          toolName: rawItem.name,
          arguments: rawItem.arguments,
        },
      };
    }

    if (eventData.name === 'tool_output' && rawItem.type === 'function_call_result') {
      return {
        type: 'tool_output',
        data: {
          toolId: this.getFunctionCallId(rawItem),
          requestId: rawItem.id,
          callId: rawItem.callId || rawItem.call_id,
          toolName: rawItem.name,
          status: rawItem.status === 'completed' ? 'completed' : 'failed',
          output: this.extractToolOutputText(rawItem.output),
        },
      };
    }

    return null;
  }

  private extractToolOutputText(output: any): string {
    if (!output) {
      return '';
    }

    if (typeof output === 'string') {
      return output;
    }

    if (typeof output === 'object' && typeof output.text === 'string') {
      return output.text;
    }

    return '';
  }

  private parseResponseFunctionCallArgumentsDelta(eventData: any): SSEEvent | null {
    const toolId = this.responseItemToToolIdMap.get(eventData.item_id);
    if (!toolId || !this.toolCallsMap.has(toolId)) {
      return null;
    }

    const toolCallInfo = this.toolCallsMap.get(toolId)!;
    toolCallInfo.arguments += eventData.delta || '';

    return {
      type: 'tool_call_delta',
      data: {
        toolId,
        callId: toolId,
        toolName: toolCallInfo.name,
        partialArguments: toolCallInfo.arguments,
      },
    };
  }

  private parseResponseFunctionCallArgumentsDone(eventData: any): SSEEvent | null {
    const toolId = this.responseItemToToolIdMap.get(eventData.item_id);
    if (!toolId || !this.toolCallsMap.has(toolId)) {
      return null;
    }

    const toolCallInfo = this.toolCallsMap.get(toolId)!;
    toolCallInfo.arguments = eventData.arguments || toolCallInfo.arguments;

    return {
      type: 'tool_call_complete',
      data: {
        toolId,
        callId: toolId,
        toolName: toolCallInfo.name,
        arguments: toolCallInfo.arguments,
      },
    };
  }

  private parseResponseOutputItemDone(eventData: any): SSEEvent | null {
    const item = eventData.item;
    if (!item) {
      return null;
    }

    if (item.type === 'reasoning') {
      this.hasSeenThinking = false;
      return { type: 'thinking_done' };
    }

    if (item.type !== 'function_call') {
      return null;
    }

    const toolId = this.getFunctionCallId(item);
    const toolName = item.name || this.toolCallsMap.get(toolId)?.name;
    if (!toolId || !toolName) {
      return null;
    }

    const toolCallInfo = this.toolCallsMap.get(toolId) || { name: toolName, arguments: '' };
    const args = item.arguments || toolCallInfo.arguments;
    this.toolCallsMap.set(toolId, { name: toolName, arguments: args });

    return {
      type: 'tool_called',
      data: {
        toolId,
        requestId: item.id,
        callId: item.callId || item.call_id,
        toolName,
        arguments: args,
      },
    };
  }

  private hydrateToolCallsFromResponseOutput(outputs: any[]) {
    outputs.forEach((output) => {
      if (output?.type !== 'function_call') {
        return;
      }

      const toolId = this.getFunctionCallId(output);
      if (!toolId || !this.toolCallsMap.has(toolId)) {
        return;
      }

      const toolCall = this.toolCallsMap.get(toolId)!;
      if (!toolCall.arguments || toolCall.arguments.trim() === '') {
        toolCall.arguments = output.arguments;
      }
    });
  }

  private getFunctionCallId(item: any): string {
    return item.callId || item.call_id || item.callID || item.id || '';
  }

  private rememberTextDeltaSignature(providerData: any, delta: string) {
    const signature = this.getTextDeltaSignature(providerData, delta);
    if (!signature) {
      return;
    }

    this.recentTextDeltaSignatures.push(signature);
    this.recentTextDeltaSignatureSet.add(signature);

    if (this.recentTextDeltaSignatures.length > 50) {
      const removed = this.recentTextDeltaSignatures.shift();
      if (removed) {
        this.recentTextDeltaSignatureSet.delete(removed);
      }
    }
  }

  private hasRecentTextDeltaSignature(eventData: any, delta: string): boolean {
    const signature = this.getTextDeltaSignature(eventData, delta);
    return Boolean(signature && this.recentTextDeltaSignatureSet.has(signature));
  }

  private getTextDeltaSignature(data: any, delta: string): string | null {
    if (data?.type !== 'response.output_text.delta') {
      return null;
    }

    return [
      data.item_id || '',
      data.output_index ?? '',
      data.content_index ?? '',
      delta,
    ].join('|');
  }
}
